import React, { useEffect } from "react";
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState, AppDispatch } from "../../store";
import {
  fetchChatHistory,
  resetChatHistory,
  fetchWorkflowDetails,
} from "../../store/chatHistorySlice";
import { loadHistoricalChat } from "../../store/userSlice";
import {
  parseHistoricalMessageContent,
  extractMessageMetadata,
} from "../../utils/agentResponseParser";
import { formatRelativeTime } from "../../utils/dateUtils";
import { getAgentNavigationUrl } from "../../utils/constants";
import styles from "./chathistorypanel.module.css";

interface ChatHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  agentName: string;
}

const ITEMS_PER_PAGE = 5;

const ChatHistoryPanel: React.FC<ChatHistoryPanelProps> = ({
  isOpen,
  onClose,
  agentName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    data: chatHistory,
    loading,
    loadingMore,
    error,
    hasMore,
    currentOffset,
    currentAgent,
  } = useSelector((state: RootState) => state.chatHistory);

  const currentAgentData = useSelector(
    (state: RootState) => state.agents.currentAgent
  );

  useEffect(() => {
    if (isOpen && agentName) {
      // Reset and fetch fresh data when opening or agent changes
      if (currentAgent !== agentName) {
        dispatch(resetChatHistory());
      }
      dispatch(
        fetchChatHistory({
          parentAgent: agentName,
          limit: ITEMS_PER_PAGE,
          offset: 0,
        }),
      );
    }
  }, [isOpen, agentName, dispatch, currentAgent]);

  const handleLoadMore = () => {
    if (!agentName) return;
    const newOffset = currentOffset + ITEMS_PER_PAGE;
    dispatch(
      fetchChatHistory({
        parentAgent: agentName,
        limit: ITEMS_PER_PAGE,
        offset: newOffset,
      }),
    );
  };

  const handleChatClick = async (workflowId: string) => {
    try {
      // Fetch workflow details
      const result = await dispatch(fetchWorkflowDetails(workflowId)).unwrap();

      // Parse assistant messages to match the format used in the chat
      const parsedMessages = result.messages.map((msg, index) => {
        if (msg.role === "assistant") {
          console.log(`Parsing assistant message ${index}:`, {
            messageId: msg.id,
            contentLength: msg.content?.length,
            contentPreview: msg.content?.substring(0, 100),
          });

          // Extract metadata from the original content
          const metadata = extractMessageMetadata(msg.content);

          // Get agent endpoint for proper workflow detection
          const agentEndpoint = currentAgentData?.endpoint;
          console.log(`Using agent endpoint: ${agentEndpoint}`);

          if (!agentEndpoint) {
            console.warn("Agent endpoint is undefined! Parser will default to campaign workflow.");
            console.log("currentAgentData:", currentAgentData);
          }

          // Parse the assistant message content with endpoint for accurate detection
          const parsedContent = parseHistoricalMessageContent(
            msg.content,
            agentEndpoint
          );

          console.log(`Parsed content for message ${index}:`, {
            length: parsedContent?.length,
            preview: parsedContent?.substring(0, 100),
          });

          return {
            ...msg,
            content: parsedContent,
            // Add extracted metadata to help with rendering
            // Convert null to undefined for type compatibility
            agentIdName: metadata.agentIdName || msg.responseType || undefined,
            chatId: metadata.chatId || undefined,
            requestId: metadata.requestId || undefined,
          };
        }
        return msg;
      });

      // Log final parsed messages before dispatching
      console.log("Final parsed messages:", parsedMessages.map(m => ({
        role: m.role,
        contentLength: m.content?.length,
        contentPreview: m.content?.substring(0, 100),
        agentIdName: m.agentIdName,
      })));

      // Load the parsed historical messages into the chat
      dispatch(
        loadHistoricalChat({
          messages: parsedMessages,
          workflowId: result.workflowId,
        })
      );

      // Close the history panel
      onClose();

      // Navigate to chat page if not already there
      const urlIdentifier =
        currentAgentData?.agent_framework_name || currentAgentData?.id;
      const chatPath = getAgentNavigationUrl(
        currentAgentData?.id || 0,
        urlIdentifier || "",
        null,
      );
      if (location.pathname !== chatPath) {
        navigate(chatPath);
      }
    } catch (error) {
      console.error("Failed to load chat history:", error);
      // Error is already handled by Redux, but you can add additional UI feedback here
    }
  };

  const formatDate = (dateString: string) => {
    return formatRelativeTime(dateString);
  };

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onClose}
      ModalProps={{
        BackdropProps: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.35)",
            left: { xs: 0, sm: "100px" },
          },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 300 },
          bgcolor: "#ffffff",
          boxShadow: "4px 0 12px rgba(0, 0, 0, 0.08)",
          ml: { xs: 0, sm: "100px" },
        },
      }}
    >
      <Box className={styles.container}>
        {/* Header */}
        <Box className={styles.header}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HistoryIcon sx={{ color: "#1e6b59" }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#2a312b" }}>
              Chat History
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close chat history"
            sx={{
              "&:hover": { bgcolor: "rgba(30, 107, 89, 0.08)" },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Box>

        {/* Agent Name */}
        <Box className={styles.agentInfo}>
          <Typography
            variant="body2"
            sx={{
              color: "#1e6b59",
              fontWeight: 600,
              fontSize: "13px",
              letterSpacing: "0.3px",
            }}
          >
            {agentName}
          </Typography>
        </Box>

        {/* Content */}
        <Box className={styles.content}>
          {loading ? (
            <Box className={styles.loadingContainer}>
              <CircularProgress size={40} sx={{ color: "#1e6b59" }} />
              <Typography variant="body2" sx={{ mt: 2, color: "#4a6b63" }}>
                Loading chat history...
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
              >
                {error}
              </Alert>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  dispatch(resetChatHistory());
                  dispatch(
                    fetchChatHistory({
                      parentAgent: agentName,
                      limit: ITEMS_PER_PAGE,
                      offset: 0,
                    })
                  );
                }}
                sx={{
                  borderColor: "#1e6b59",
                  color: "#1e6b59",
                  "&:hover": {
                    borderColor: "#00d2aa",
                    bgcolor: "#e8faf3",
                  },
                }}
              >
                Try Again
              </Button>
            </Box>
          ) : chatHistory.length === 0 ? (
            <Box className={styles.emptyState}>
              <HistoryIcon
                sx={{
                  fontSize: 72,
                  color: "#d1e0db",
                  mb: 2,
                  opacity: 0.5,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: "#4a6b63",
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                No Chat History
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#8ea39a",
                  textAlign: "center",
                  maxWidth: "280px",
                }}
              >
                Your conversations with this agent will appear here
              </Typography>
            </Box>
          ) : (
            <>
              <List sx={{ p: 0 }}>
                {chatHistory.map((item) => (
                  <ListItem key={item.workflowId} disablePadding>
                    <ListItemButton
                      onClick={() => handleChatClick(item.workflowId)}
                      className={styles.listItem}
                      sx={{
                        borderBottom: "1px solid #e6f3ec",
                        borderLeft: "3px solid transparent",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: "#f0f9f6",
                          borderLeftColor: "#1e6b59",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: "#2a312b",
                              mb: 0.5,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              lineHeight: 1.4,
                              transition: "color 0.2s ease",
                              "&:hover": {
                                color: "#1e6b59",
                              },
                            }}
                          >
                            {item.title}
                          </Typography>
                        }
                        secondary={
                          <Box component="span" sx={{ mt: 0.5, display: "block" }}>
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{
                                color: "#8ea39a",
                                display: "block",
                                mb: 0.5,
                              }}
                            >
                              {formatDate(item.createdDate)}
                            </Typography>
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{
                                color: "#4a6b63",
                                bgcolor: "#e8faf3",
                                px: 1,
                                py: 0.25,
                                borderRadius: "4px",
                                display: "inline-block",
                              }}
                            >
                              {item.messageCount} message
                              {item.messageCount !== 1 ? "s" : ""}
                            </Typography>
                          </Box>
                        }
                        secondaryTypographyProps={{
                          component: "div",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {/* Load More Button */}
              {hasMore && (
                <Box className={styles.loadMoreContainer}>
                  <Button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderColor: "#1e6b59",
                      color: "#1e6b59",
                      fontWeight: 500,
                      padding: "10px 16px",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#00d2aa",
                        bgcolor: "#e8faf3",
                      },
                      "&:disabled": {
                        borderColor: "#d1e0db",
                        color: "#8ea39a",
                      },
                    }}
                  >
                    {loadingMore ? (
                      <>
                        <CircularProgress
                          size={18}
                          sx={{ mr: 1, color: "#1e6b59" }}
                        />
                        Loading more...
                      </>
                    ) : (
                      "Load More Chats"
                    )}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ChatHistoryPanel;
